import AssessmentOutlined from "@mui/icons-material/AssessmentOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Alert, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import React, { useState } from "react";
import CompetitiveAssessmentContent from "./CompetitiveAssessmentContent";
import { AssessmentQueryType, AssessmentResult } from "./competitiveAssessmentUtils";
import { insightCardTitleStyles, insightCardToggleStyles } from "./insightCardStyles";

type CompetitiveAssessmentCardProps = {
  id: string;
  assessment: AssessmentResult;
  queryType?: AssessmentQueryType;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  emptyMessage: string;
  detailsMaxHeight?: string;
  embedded?: boolean;
};

const CompetitiveAssessmentCard: React.FC<CompetitiveAssessmentCardProps> = ({
  id,
  assessment,
  queryType = "CompetitiveAssessment",
  isFetching,
  isError,
  onRetry,
  emptyMessage,
  detailsMaxHeight,
  embedded = false
}) => {
  const [expanded, setExpanded] = useState(true);
  const message = isFetching
    ? "Loading competitive assessment..."
    : isError
      ? "Unable to load the competitive assessment. Please try again."
      : assessment.status === "error"
        ? assessment.message
        : emptyMessage;
  const detailsId = `${id}-details`;
  const background = "linear-gradient(90deg, #F7FBFF 0%, #EEF6FD 100%)";

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        bgcolor: "#FFFFFF",
        boxShadow: "0 1px 2px rgba(0,0,0,.08), 0 8px 24px rgba(15,45,75,.06)",
        overflow: "hidden",
        borderLeft: "4px solid #0F6CBD",
        flexShrink: 0
      }}
    >
      {!embedded && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "grid",
            gridTemplateColumns: "32px minmax(0, 1fr) auto",
            columnGap: 1.25,
            alignItems: "center",
            bgcolor: "#F6FAFE",
            background
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              gridColumn: 1,
              gridRow: 1,
              width: 30,
              height: 30,
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              bgcolor: "#DDEFFF",
              color: "#0F6CBD"
            }}
          >
            <AssessmentOutlined sx={{ fontSize: 17 }} />
          </Box>
          <Typography component="h2" sx={{ ...insightCardTitleStyles, gridColumn: 2, gridRow: 1 }}>
            Competitive Assessment
          </Typography>
          <Button
            size="small"
            variant="contained"
            disableElevation
            aria-expanded={expanded}
            aria-controls={detailsId}
            startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setExpanded((current) => !current)}
            sx={{ ...insightCardToggleStyles, gridColumn: 3, gridRow: 1 }}
          >
            {expanded ? "Show less" : "Show more"}
          </Button>
        </Box>
      )}
      {!embedded && !expanded && (
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            display: "grid",
            gridTemplateColumns: "32px minmax(0, 1fr)",
            columnGap: 1.25,
            bgcolor: "#F6FAFE",
            background
          }}
        >
          <Typography
            sx={{
              gridColumn: 2,
              minWidth: 0,
              overflow: "hidden",
              color: "#616161",
              fontSize: 14,
              lineHeight: 1.45,
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {!isFetching && !isError && assessment.status === "ready"
              ? assessment.data.executiveSummary || "No summary is available."
              : message}
          </Typography>
        </Box>
      )}
      {(embedded || expanded) && (
        <Box
          id={detailsId}
          sx={{
            p: 2,
            bgcolor: "#FFFFFF",
            borderTop: embedded ? undefined : "1px solid #E0E0E0",
            maxHeight: detailsMaxHeight,
            overflowY: detailsMaxHeight ? "auto" : undefined
          }}
        >
          {isFetching ? (
            <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: 13 }}>{message}</Typography>
            </Box>
          ) : isError || assessment.status === "error" ? (
            <Alert
              severity="error"
              action={
                isError ? (
                  <Button color="inherit" size="small" onClick={onRetry}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {message}
            </Alert>
          ) : assessment.status === "ready" ? (
            <CompetitiveAssessmentContent assessment={assessment.data} queryType={queryType} />
          ) : (
            <Typography sx={{ color: "#616161", fontSize: 13 }}>{message}</Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CompetitiveAssessmentCard;
